import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { HiBookOpen, HiHome, HiLogout, HiUser } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../components/ui/Card';
import { courseService } from '../services/courseService';
import { useAuthStore } from '../stores/authStore';
import '../styles/Dashboard.css';
import '../styles/DashboardMainContent.css';
import '../styles/SideNavBar.css';
import './MyCoursesPage.css';

const MyCoursesPage = () => {
  const navigate = useNavigate();
  const { currentUser, logout, isAuthenticated } = useAuthStore();
  const [activeTab, setActiveTab] = useState('my-courses');
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Navigation items for the sidebar
  const navigationItems = [
    {
      id: 'home',
      label: 'Home',
      icon: HiHome,
      path: '/'
    },
    {
      id: 'all-courses',
      label: 'All Courses',
      icon: HiBookOpen,
      path: '/dashboard'
    },
    {
      id: 'my-courses',
      label: 'My Courses',
      icon: HiBookOpen,
      path: '/my-courses'
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: HiUser,
      path: '/profile'
    }
  ];

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const tab = navigationItems.find(item => item.id === tabId);
    if (tab && tab.path) {
      navigate(tab.path);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Successfully logged out');
  };

  const handleCourseClick = (course) => {
    const courseId = course.courseId || course.id;
    navigate(`/course/${courseId}`, {
      state: {
        course: {
          ...course,
          id: courseId
        },
        isPurchased: true,
        purchaseInfo: {
          purchaseId: course.purchaseId,
          purchaseDate: course.purchaseDate,
          accessExpiryDate: course.accessExpiryDate,
          pricePaid: course.pricePaid
        }
      }
    });
  };

  const fetchPurchasedCourses = async () => {
    if (!currentUser?.id) {
      console.log('No current user ID found');
      return;
    }

    try {
      setIsLoading(true);
      const purchasedCourses = await courseService.getPurchasedCourses(currentUser.id);

      // Filter courses: Only show courses with status: "ACTIVE" AND isPurchase: true
      const filteredAndFormattedCourses = purchasedCourses
        .filter(course => {
          const hasValidCourseData = Array.isArray(course.data) &&
            course.data.length > 0 &&
            course.data[0].title &&
            course.data[0].description !== undefined;
          if (!hasValidCourseData) {
            console.warn('Skipping invalid course data:', course);
            return false;
          }

          const courseData = course.data[0];
          // Apply filtering: status must be "ACTIVE" AND isPurchase must be true
          const isActive = courseData.status === 'ACTIVE';
          const isPurchased = course.isPurchase === true || courseData.isPurchase === true;

          if (!isActive || !isPurchased) {
            console.log(`Filtering out course ${courseData.id}: status=${courseData.status}, isPurchase=${isPurchased}`);
            return false;
          }

          return true;
        })
        .map(course => {
          const courseData = course.data[0];
          const imageUrl = courseData.displayPicture || '/default-course.jpg';

          return {
            id: courseData.id,
            courseId: courseData.id,
            title: courseData.title,
            description: courseData.description,
            originalPrice: course.originalPrice || courseData.originalPrice,
            offerPrice: courseData.offerPrice,
            price: course.pricePaid || courseData.offerPrice || 0,
            image: imageUrl,
            duration: '4 months',
            status: courseData.status,
            isPurchase: course.isPurchase || courseData.isPurchase,
            purchaseId: course.purchaseId,
            purchaseDate: course.purchaseDate,
            accessExpiryDate: course.accessExpiryDate
          };
        });

      console.log(`Filtered courses: ${filteredAndFormattedCourses.length} out of ${purchasedCourses.length} total courses`);
      setCourses(filteredAndFormattedCourses);
    } catch (error) {
      console.error('Error fetching purchased courses:', error);
      setError('Failed to load purchased courses. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.id) {
      fetchPurchasedCourses();
    }
  }, [currentUser?.id]);

  // Loading state
  if (isLoading) {
    return (
      <div className="dashboard-container">
        <div className="side-navbar">
          <div className="navbar-logo">
            <img src="/images/Somayya-Academy-Logo-for-Website-Home.001.png" alt="Somayya Academy" />
          </div>
          <nav className="navbar-nav">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <IconComponent className="nav-icon" />
                  <span className="nav-label body3">{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="navbar-logout">
            <button onClick={handleLogout} className="logout-btn">
              <HiLogout className="logout-icon" />
              <span className="logout-label body3">Logout</span>
            </button>
          </div>
        </div>
        <div className="dashboard-main-content">
          <div className="content-container">
            <h2 className="h2 text-primary">My Courses</h2>
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading your courses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="side-navbar">
          <div className="navbar-logo">
            <img src="/images/Somayya-Academy-Logo-for-Website-Home.001.png" alt="Somayya Academy" />
          </div>
          <nav className="navbar-nav">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <IconComponent className="nav-icon" />
                  <span className="nav-label body3">{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="navbar-logout">
            <button onClick={handleLogout} className="logout-btn">
              <HiLogout className="logout-icon" />
              <span className="logout-label body3">Logout</span>
            </button>
          </div>
        </div>
        <div className="dashboard-main-content">
          <div className="content-container">
            <h2 className="h2 text-primary">My Courses</h2>
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // No courses state
  if (!courses.length) {
    return (
      <div className="dashboard-container">
        <div className="side-navbar">
          <div className="navbar-logo">
            <img src="/images/Somayya-Academy-Logo-for-Website-Home.001.png" alt="Somayya Academy" />
          </div>
          <nav className="navbar-nav">
            {navigationItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                >
                  <IconComponent className="nav-icon" />
                  <span className="nav-label body3">{item.label}</span>
                </button>
              );
            })}
          </nav>
          <div className="navbar-logout">
            <button onClick={handleLogout} className="logout-btn">
              <HiLogout className="logout-icon" />
              <span className="logout-label body3">Logout</span>
            </button>
          </div>
        </div>
        <div className="dashboard-main-content">
          <div className="content-container">
            <h2 className="h2 text-primary">My Courses</h2>
            <div className="no-courses-message">
              <p>No active purchased courses found.</p>

            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main content with courses
  return (
    <div className="dashboard-container">
      <div className="side-navbar">
        <div className="navbar-logo">
          <img src="/images/Somayya-Academy-Logo-for-Website-Home.001.png" alt="Somayya Academy" />
        </div>
        <nav className="navbar-nav">
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
              >
                <IconComponent className="nav-icon" />
                <span className="nav-label body3">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="navbar-logout">
          <button onClick={handleLogout} className="logout-btn">
            <HiLogout className="logout-icon" />
            <span className="logout-label body3">Logout</span>
          </button>
        </div>
      </div>

      <div className="dashboard-main-content">
        <div className="content-container">
          <h2 className="h2 text-primary">My Courses</h2>
          <div className="courses-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '2rem',
            padding: '1rem 0'
          }}>
            {courses.map((course) => (
              <Card
                key={course.id}
                title={course.title}
                price={course.price}
                originalPrice={course.originalPrice}
                offerPrice={course.offerPrice}
                duration={course.duration}
                image={course.image}
                status={course.status}
                onCardClick={() => handleCourseClick(course)}
                className="course-card"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// PropTypes for course data structure validation
MyCoursesPage.courseShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  originalPrice: PropTypes.number,
  offerPrice: PropTypes.number,
  price: PropTypes.number,
  image: PropTypes.string,
  duration: PropTypes.string,
  status: PropTypes.oneOf(['ACTIVE', 'INACTIVE', 'PENDING']).isRequired,
  isPurchase: PropTypes.bool.isRequired,
  purchaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  purchaseDate: PropTypes.string,
  accessExpiryDate: PropTypes.string
});

export default MyCoursesPage;
